import React, {
  useState,
  useMemo,
  useCallback,
  ReactNode,
  useEffect,
  useRef,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useLibraryDataSource } from "../hooks/useLibraryDataSource";
import { useInfiniteScroll } from "../hooks/useInfiniteScroll";
import { useAppStore } from "@/core/store/useAppStore";
import { Button } from "@/components/ui/Button";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import {
  MediaStage,
  MediaFilterTab,
} from "@/core/types/mediaTypes";
import { ChevronDownIcon, PlusIcon } from "@/components/icons";
import { ENTITY_CONFIG } from "@/core/entityConfig";
import { TabGroup, TabItem, TabGroupConfig } from "@/components/ui/TabGroup";
import { Text } from "@/components/ui/Text";
import {
  ScrollableContainer,
  MediaGrid,
  MediaGridMobile,
} from "@/components/MediaContainer";
import { MediaShareModal } from "./MediaShareModal";
import { BulkStageChangeDropdown } from "./BulkStageChangeDropdown";
import { BulkRotateDropdown } from "./BulkRotateDropdown";
import { FilterDropdown } from "./FilterDropdown";
import { usePostModals } from "@/domains/PostCreator/hooks/usePostModals";
import { CollectionsSidebar } from "@/components/CollectionSelector";
import { UploadMediaModal } from "./UploadMediaModal";
import { AddToCollectionModal } from "./AddToCollectionModal";
import { GlobeIcon, SpinnerIcon } from "@/components/icons";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { apiClient } from "@/services/apiClient";
import { mediaService } from "@/core/services/mediaService";
import { useCollectionMediaTypeCounts } from "@/core/MediaLibrary/hooks/useCollectionItemsQuery";
import { PageLayout, PageHeader, PageSection } from "@/components/PageLayout";
import { MediaContentType } from "@/core/types/mediaContentType";

/**
 * UI tab ids for the library (hyphenated, e.g. "images", "post-videos",
 * "assets"). These are distinct from the storage/wire `MediaContentType`
 * values — a tab maps to one or more wire content types when fetching.
 */
export type ContentType = "images" | "post-videos" | "stories" | "reels" | "videos" | "audio" | "assets";

// Static tab configurations - moved outside component to prevent re-creation on every render
const STAGE_TABS: TabItem<MediaFilterTab>[] = [
  { id: MediaFilterTab.ALL, label: "Tutti" },
  {
    id: MediaFilterTab.DRAFT,
    label: "Bozza",
    icon: <div className="w-2 h-2 rounded-full bg-yellow-500"></div>,
  },
  {
    id: MediaFilterTab.PENDING_APPROVAL,
    label: "In attesa",
    icon: <div className="w-2 h-2 rounded-full bg-orange-500"></div>,
  },
  {
    id: MediaFilterTab.APPROVED,
    label: "Approvato",
    icon: <div className="w-2 h-2 rounded-full bg-green-500"></div>,
  },
  {
    id: MediaFilterTab.LEGACY_USED,
    label: "Usato",
    icon: <div className="w-2 h-2 rounded-full bg-purple-500"></div>,
  },
  {
    id: MediaFilterTab.ARCHIVED,
    label: "Archiviato",
    icon: <div className="w-2 h-2 rounded-full bg-blue-500"></div>,
  },
  {
    id: MediaFilterTab.DISCARDED,
    label: "Scartato",
    icon: <div className="w-2 h-2 rounded-full bg-gray-500"></div>,
  },
];

const STAGE_TAB_CONFIG: TabGroupConfig<MediaFilterTab> = {
  variant: "navigation",
  size: "sm",
  layout: "horizontal",
  hasBackground: true,
};

export const CONTENT_TYPE_TABS: TabItem<ContentType>[] = [
  { id: "images", label: "Images" },
  { id: "post-videos", label: "Post Videos" },
  { id: "videos", label: "Videos" },
  { id: "stories", label: "Stories" },
  { id: "reels", label: "Reels" },
  { id: "audio", label: "Audio" },
  { id: "assets", label: "Assets" },
];

export const CONTENT_TYPE_TAB_CONFIG: TabGroupConfig<ContentType> = {
  variant: "navigation",
  size: "sm",
  layout: "horizontal",
  hasBackground: true,
};

interface LibraryLayoutProps {
  contentTypes: MediaContentType[];
  activeContentType: ContentType;
  onContentTypeChange: (type: ContentType) => void;
  renderGrid: (
    items: MediaItem[],
    handleStageChange: (updatedItem: MediaItem, oldStage: MediaStage) => void,
    handleItemClick?: (item: MediaItem) => void,
    selectionMode?: {
      isActive: boolean;
      selectedIds: number[];
      onToggle: (id: number, event: React.MouseEvent) => void;
    },
    onAddToCollection?: (mediaItemId: number) => void,
  ) => ReactNode;
  extraContent?: ReactNode;
  onShareWithClient?: () => void;
  onEnterReviewMode?: (selectedIds: number[]) => void;
}

export const LibraryLayout: React.FC<LibraryLayoutProps> = ({
  contentTypes,
  activeContentType,
  onContentTypeChange,
  renderGrid,
  extraContent,
  onShareWithClient,
  onEnterReviewMode,
}) => {
  const {
    selectedClient,
    collections: collectionsState,
    getActiveCollection,
    enterCollectionMode,
    exitCollectionMode,
    setActiveCollection,
    fetchActiveCollectionItems,
  } = useAppStore();
  const addToast = useAppStore((state) => state.addToast);
  const { openCreateModalWithMedia } = usePostModals();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const lastSelectedIndexRef = useRef<number | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showAddToCollectionModal, setShowAddToCollectionModal] = useState(false);
  const [addToCollectionItemId, setAddToCollectionItemId] = useState<number | null>(null);

  const activeCollection = getActiveCollection();
  const { isCollectionMode, collections, activeCollectionId } = collectionsState;

  // Collection media type tab state (Images / Videos when viewing a collection)
  // Declared early so URL sync can reference it
  const [collectionTab, setCollectionTab] = useState<"images" | "videos">(
    () => {
      const typeParam = searchParams.get("type");
      return typeParam === "videos" ? "videos" : "images";
    }
  );

  // Fetch collection items when active collection changes
  useEffect(() => {
    if (activeCollectionId && selectedClient) {
      fetchActiveCollectionItems(selectedClient.id);
    }
  }, [activeCollectionId, selectedClient, fetchActiveCollectionItems]);

  // Restore collection mode from URL params on mount
  useEffect(() => {
    const collectionParam = searchParams.get("collection");
    if (collectionParam && !activeCollectionId) {
      enterCollectionMode();
      setActiveCollection(collectionParam);
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync collection state to URL params
  useEffect(() => {
    if (isCollectionMode && activeCollectionId) {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set("collection", activeCollectionId);
          newParams.set("type", collectionTab);
          return newParams;
        },
        { replace: true }
      );
    } else {
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.delete("collection");
          newParams.delete("type");
          return newParams;
        },
        { replace: true }
      );
    }
  }, [isCollectionMode, activeCollectionId, collectionTab, setSearchParams]);

  // Enter collection mode (sidebar only, no auto-select)
  const handleEnterCollectionMode = useCallback(() => {
    enterCollectionMode();
  }, [enterCollectionMode]);

  // Public images filter
  const getOnlyPublicFromUrl = (): boolean => {
    return searchParams.get("public_only") === "true";
  };

  const [onlyPublic, setOnlyPublic] = useState(getOnlyPublicFromUrl);

  useEffect(() => {
    const urlValue = getOnlyPublicFromUrl();
    if (urlValue !== onlyPublic) {
      setOnlyPublic(urlValue);
    }
  }, [searchParams]);

  const handleOnlyPublicChange = useCallback((checked: boolean) => {
    setOnlyPublic(checked);
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        if (checked) {
          newParams.set("public_only", "true");
        } else {
          newParams.delete("public_only");
        }
        return newParams;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  // Non-lead carousel filter - shows only secondary images in carousels (position > 0)
  const getOnlyNonLeadCarouselFromUrl = (): boolean => {
    return searchParams.get("non_lead_carousel") === "true";
  };

  const [onlyNonLeadCarousel, setOnlyNonLeadCarousel] = useState(getOnlyNonLeadCarouselFromUrl);

  // Sync onlyNonLeadCarousel with URL changes
  useEffect(() => {
    const urlValue = getOnlyNonLeadCarouselFromUrl();
    if (urlValue !== onlyNonLeadCarousel) {
      setOnlyNonLeadCarousel(urlValue);
    }
  }, [searchParams]);

  const handleNonLeadCarouselChange = useCallback((checked: boolean) => {
    setOnlyNonLeadCarousel(checked);
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        if (checked) {
          newParams.set("non_lead_carousel", "true");
        } else {
          newParams.delete("non_lead_carousel");
        }
        return newParams;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  // Website tag filter with URL persistence (comma-separated "tags" param)
  const getSelectedTagsFromUrl = (): string[] => {
    const param = searchParams.get("tags");
    if (!param) return [];
    return param.split(",").map(t => t.trim()).filter(Boolean);
  };

  const [selectedTags, setSelectedTags] = useState<string[]>(getSelectedTagsFromUrl);

  useEffect(() => {
    const urlTags = getSelectedTagsFromUrl();
    const current = selectedTags.join(",");
    const fromUrl = urlTags.join(",");
    if (current !== fromUrl) {
      setSelectedTags(urlTags);
    }
  }, [searchParams]);

  const handleSelectedTagsChange = useCallback((tags: string[]) => {
    setSelectedTags(tags);
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        if (tags.length > 0) {
          newParams.set("tags", tags.join(","));
        } else {
          newParams.delete("tags");
        }
        return newParams;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  // Bulk make-public state
  const [isBulkMakingPublic, setIsBulkMakingPublic] = useState(false);
  const [showBulkPublicConfirm, setShowBulkPublicConfirm] = useState(false);
  const [bulkPublicTarget, setBulkPublicTarget] = useState<"selection" | "filtered">("selection");

  // Initialize activeTab from URL or default to DRAFT
  const getInitialTab = (): MediaFilterTab => {
    const stageParam = searchParams.get("stage");
    if (
      stageParam &&
      Object.values(MediaFilterTab).includes(stageParam as MediaFilterTab)
    ) {
      return stageParam as MediaFilterTab;
    }
    return MediaFilterTab.DRAFT;
  };

  const [activeTab, setActiveTab] = useState<MediaFilterTab>(getInitialTab);

  // Update URL when tab changes
  const handleTabChange = useCallback(
    (tab: MediaFilterTab) => {
      setActiveTab(tab);
      setSearchParams(
        (prev) => {
          const newParams = new URLSearchParams(prev);
          newParams.set("stage", tab);
          return newParams;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Stage filter from active tab
  const stageFilter = useMemo(() => {
    return activeTab === MediaFilterTab.ALL
      ? undefined
      : [activeTab as unknown as MediaStage];
  }, [activeTab]);

  // Collection media type filter derived from tab
  const collectionMediaType = useMemo(() => {
    if (!isCollectionMode || !activeCollectionId) return undefined;
    return collectionTab === "images" ? "photo" : "video";
  }, [isCollectionMode, activeCollectionId, collectionTab]);

  // Unified data source - switches between library and collection mode internally
  const dataSource = useLibraryDataSource({
    clientId: selectedClient?.id || "",
    contentTypes,
    stageFilter,
    onlyPublic,
    onlyNonLeadCarousel,
    websiteTags: selectedTags.length > 0 ? selectedTags : undefined,
    collectionId: isCollectionMode && activeCollectionId ? activeCollectionId : undefined,
    collectionMediaType,
  });

  const currentItems = dataSource.items;

  // Infinite scroll: load the next page as the sentinel nears the bottom of the
  // grid, so older items surface without hunting for a "Load more" button.
  const scrollRootRef = useRef<HTMLDivElement>(null);
  const infiniteScrollSentinelRef = useInfiniteScroll({
    rootRef: scrollRootRef,
    hasMore: dataSource.hasMore,
    isLoadingMore: dataSource.isLoadingMore,
    loadMore: dataSource.loadMore,
  });

  // Fetch media type counts to determine default collection tab
  const { data: mediaTypeCounts } = useCollectionMediaTypeCounts(
    isCollectionMode && activeCollectionId ? selectedClient?.id : undefined,
    isCollectionMode ? activeCollectionId ?? undefined : undefined,
  );

  useEffect(() => {
    if (!mediaTypeCounts) return;
    const photoCount = mediaTypeCounts.photo ?? 0;
    setCollectionTab(photoCount > 0 ? "images" : "videos");
  }, [mediaTypeCounts, activeCollectionId]);

  const handleStageChange = useCallback(
    (updatedItem: MediaItem, oldStage: MediaStage) => {
      console.log(`Stage changed: ${updatedItem.getName()} from ${oldStage} to ${updatedItem.stage}`);
    },
    [],
  );

  /**
   * Fetch all distinct website tags for this client from the backend.
   * Independent of media pagination so we always see the full tag set.
   */
  const { data: availableTags = [] } = useQuery({
    queryKey: ["website-tags", selectedClient?.id],
    queryFn: async () => {
      if (!selectedClient) return [];
      const response = await apiClient.get<{ tags: string[] }>(
        `/client/${selectedClient.id}/media/website-tags`,
      );
      return response.tags;
    },
    enabled: !!selectedClient,
    staleTime: 60 * 1000,
  });

  // Selection handlers
  const toggleSelectionMode = useCallback(() => {
    setIsSelectionMode(prev => !prev);
    setSelectedIds([]);
    lastSelectedIndexRef.current = null;
  }, []);

  const toggleSelection = useCallback((mediaId: number, event?: React.MouseEvent) => {
    const clickedIndex = currentItems.findIndex(item => item.id === mediaId);

    if (event?.shiftKey && lastSelectedIndexRef.current !== null && clickedIndex !== -1) {
      const start = Math.min(lastSelectedIndexRef.current, clickedIndex);
      const end = Math.max(lastSelectedIndexRef.current, clickedIndex);
      const rangeIds = currentItems
        .slice(start, end + 1)
        .map(item => item.id);

      setSelectedIds(prev => {
        const merged = new Set(prev);
        rangeIds.forEach(id => merged.add(id));
        return Array.from(merged);
      });
    } else {
      setSelectedIds(prev => {
        const exists = prev.indexOf(mediaId);
        if (exists !== -1) {
          return prev.filter(id => id !== mediaId);
        } else {
          return [...prev, mediaId];
        }
      });
    }

    if (clickedIndex !== -1) {
      lastSelectedIndexRef.current = clickedIndex;
    }
  }, [currentItems]);

  const deselectAll = useCallback(() => {
    setSelectedIds([]);
    lastSelectedIndexRef.current = null;
  }, []);

  // Exit selection mode when switching tabs or content types
  useEffect(() => {
    setIsSelectionMode(false);
    setSelectedIds([]);
    lastSelectedIndexRef.current = null;
  }, [activeTab, activeContentType]);

  // selectAll depends on currentItems, so must come after
  const selectAll = useCallback(() => {
    setSelectedIds(currentItems.map(item => item.id));
    setIsSelectionMode(true);
  }, [currentItems]);

  const handleBatchProcess = useCallback(async () => {
    if (selectedIds.length === 0 || !selectedClient) return;

    setIsProcessing(true);
    try {
      const { apiClient } = await import('@/services/apiClient');
      const response = await apiClient.post('/admin/media/batch-analyze', {
        clientId: selectedClient.id,
        mediaItemIds: selectedIds,
      }, {
        timeout: 120000, // 2 min for AI batch processing
      });

      console.log('Batch processing result:', response.data);

      // Refresh to show updated AI scores
      await dataSource.refresh();

      deselectAll();
      toggleSelectionMode();
    } catch (err) {
      console.error('Failed to process batch:', err);
    } finally {
      setIsProcessing(false);
    }
  }, [selectedIds, selectedClient, dataSource, deselectAll, toggleSelectionMode]);

  const handleAddToCollection = useCallback((mediaItemId: number) => {
    setAddToCollectionItemId(mediaItemId);
    setShowAddToCollectionModal(true);
  }, []);

  const handleCreatePost = useCallback(() => {
    // Map from selectedIds to preserve selection order
    const itemMap = new Map(currentItems.map(item => [item.id, item]));
    const selectedItems = selectedIds
      .map(id => itemMap.get(id))
      .filter((item): item is MediaItem => item !== undefined);
    openCreateModalWithMedia(selectedItems);
    setIsSelectionMode(false);
    setSelectedIds([]);
  }, [currentItems, selectedIds, openCreateModalWithMedia]);

  /**
   * Call bulk-make-public endpoint with given media item IDs.
   * Used both by the selection toolbar and the "Make All Public" tag filter button.
   */
  const handleBulkMakePublic = useCallback(async (ids: number[]) => {
    if (ids.length === 0 || !selectedClient) return;
    setIsBulkMakingPublic(true);
    // The backend caps a single request at 200 ids, so split larger sets
    // into chunks. Each chunk enqueues one make-public job.
    const CHUNK = 200;
    const chunks: number[][] = [];
    for (let i = 0; i < ids.length; i += CHUNK) {
      chunks.push(ids.slice(i, i + CHUNK));
    }
    let failed = 0;
    try {
      for (const chunk of chunks) {
        try {
          await apiClient.post(
            `/client/${selectedClient.id}/media/bulk-make-public`,
            { media_item_ids: chunk },
            { timeout: 120000 },
          );
        } catch (err) {
          failed += chunk.length;
          console.error("Bulk make-public chunk failed:", err);
        }
      }
    } finally {
      setIsBulkMakingPublic(false);
    }

    if (failed > 0) {
      addToast({
        message: `Failed to start make-public for ${failed} of ${ids.length} item(s). The rest are processing.`,
        type: "error",
        duration: 6000,
      });
    } else {
      addToast({
        message: `Making ${ids.length} item(s) public. They'll update as each finishes.`,
        type: "success",
        duration: 4000,
      });
    }
  }, [selectedClient, addToast]);

  // Resolve EVERY non-public item matching the active tag filter, not just
  // the pages currently loaded in the grid. The old code read `currentItems`
  // (loaded pages only), so items below the scroll fold were silently
  // skipped. Stream the full filtered set from the server instead.
  const collectFilteredNonPublicIds = useCallback(async (): Promise<number[]> => {
    if (!selectedClient) return [];
    const websiteTags = selectedTags.length > 0 ? selectedTags : undefined;
    const seen = new Map<number, boolean>();
    for (const contentType of contentTypes) {
      const items = await mediaService.streamMediaItems(selectedClient.id, {
        contentType,
        stageFilter,
        websiteTags,
      });
      for (const item of items) {
        if (item.publicStatus !== "public") seen.set(item.id, true);
      }
    }
    return Array.from(seen.keys());
  }, [selectedClient, selectedTags, contentTypes, stageFilter]);

  const handleBulkPublicConfirm = useCallback(async () => {
    setShowBulkPublicConfirm(false);
    if (bulkPublicTarget === "selection") {
      await handleBulkMakePublic(selectedIds);
    } else {
      const ids = await collectFilteredNonPublicIds();
      await handleBulkMakePublic(ids);
    }
  }, [bulkPublicTarget, selectedIds, collectFilteredNonPublicIds, handleBulkMakePublic]);

  if (!selectedClient) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <div className="text-center">
          <svg
            className="w-16 h-16 mx-auto mb-4 text-gray-300"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 9a2 2 0 012-2m0 0V5a2 2 0 012-2h6m-6 4a2 2 0 100 4 2 2 0 000-4z"
            />
          </svg>
          <Text variant="h3">No client selected</Text>
          <Text variant="bodySmall" color="muted" className="mt-1">
            Select a client to view their library
          </Text>
        </div>
      </div>
    );
  }

  return (
    <PageLayout>
      <PageHeader>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div className="inline-block">
              <TabGroup
                items={CONTENT_TYPE_TABS}
                activeId={activeContentType}
                onTabChange={onContentTypeChange}
                config={CONTENT_TYPE_TAB_CONFIG}
              />
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowUploadModal(true)}
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              Upload
            </Button>

            {/* Collection mode header */}
            {isCollectionMode && activeCollection && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-cyan-50 border border-cyan-200 rounded-lg">
                <svg className="w-4 h-4 text-cyan-600" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                </svg>
                <span className="text-sm font-medium text-cyan-900 max-w-[200px] truncate" title={activeCollection.name || "Collection"}>
                  {activeCollection.name || "Collection"}
                </span>
                <span className="text-xs text-cyan-600">
                  ({activeCollection.itemCount} items)
                </span>
              </div>
            )}

            {activeTab === MediaFilterTab.PENDING_APPROVAL &&
              onShareWithClient && (
                <button
                  onClick={onShareWithClient}
                  className="p-2 text-purple-600 hover:text-purple-700 hover:bg-purple-50 rounded-lg transition-all duration-200"
                  title="Share with Client for Approval"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"
                    />
                  </svg>
                </button>
              )}

          </div>

          <div className="flex items-center gap-3">
            {/* Stage tabs vs Collection media type tabs */}
            {isCollectionMode && activeCollectionId ? (
              <div className="inline-block">
                <TabGroup
                  items={[
                    {
                      id: "images" as const,
                      label: `Images${mediaTypeCounts?.photo ? ` (${mediaTypeCounts.photo})` : ""}`,
                    },
                    {
                      id: "videos" as const,
                      label: `Videos${mediaTypeCounts?.video ? ` (${mediaTypeCounts.video})` : ""}`,
                    },
                  ]}
                  activeId={collectionTab}
                  onTabChange={setCollectionTab}
                  config={STAGE_TAB_CONFIG}
                />
              </div>
            ) : (
              <div className="inline-block">
                <TabGroup
                  items={STAGE_TABS}
                  activeId={activeTab}
                  onTabChange={handleTabChange}
                  config={STAGE_TAB_CONFIG}
                />
              </div>
            )}

            {/* Collection mode button */}
            {!isCollectionMode ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleEnterCollectionMode}
              >
                Collections
              </Button>
            ) : (
              <Button
                variant="secondary"
                size="sm"
                onClick={exitCollectionMode}
              >
                Exit Collections
              </Button>
            )}

            {(activeContentType === "videos" || activeContentType === "images" || activeContentType === "reels") && onEnterReviewMode && !isCollectionMode && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onEnterReviewMode(selectedIds)}
                disabled={currentItems.length === 0}
              >
                Enter Review Mode
              </Button>
            )}
          </div>
        </div>
      </PageHeader>

      <PageSection.Canvas>
        <div className={`h-full ${isCollectionMode ? 'flex' : 'grid gap-4'}`} style={isCollectionMode ? {} : { gridTemplateRows: 'auto auto 1fr' }}>
        {/* Main content area - needs flex wrapper in collection mode for sidebar */}
        <div className={isCollectionMode ? 'flex-1 flex flex-col gap-4 min-w-0' : 'contents'}>

      {/* Error State */}
      {dataSource.error && (
        <div className="flex items-center justify-center py-12 shrink-0">
          <div className="text-center">
            <svg
              className="w-16 h-16 mx-auto mb-4 text-red-300"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <Text variant="body" color="red" weight="medium" className="mb-2">
              Failed to load media
            </Text>
            <Text variant="bodySmall" color="red" className="mb-4">
              {dataSource.error}
            </Text>
            <Button
              variant="secondary"
              size="sm"
              onClick={dataSource.refresh}
            >
              Try Again
            </Button>
          </div>
        </div>
      )}

      {/* Selection Toolbar - Apple-style */}
      <div className="min-h-0 flex flex-col gap-3">
          <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3">
            <div className="flex items-center gap-4">
              {!isSelectionMode ? (
                <>
                  <button
                    onClick={toggleSelectionMode}
                    className="text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors"
                  >
                    Select
                  </button>

                  <div className="h-5 w-px bg-gray-300" />
                  <FilterDropdown
                    onlyNonLeadCarousel={onlyNonLeadCarousel}
                    onNonLeadCarouselChange={handleNonLeadCarouselChange}
                    onlyPublic={onlyPublic}
                    onOnlyPublicChange={handleOnlyPublicChange}
                    contentType={activeContentType}
                    availableTags={availableTags}
                    selectedTags={selectedTags}
                    onSelectedTagsChange={handleSelectedTagsChange}
                  />

                  {selectedTags.length > 0 && (
                    <button
                      onClick={() => {
                        setBulkPublicTarget("filtered");
                        setShowBulkPublicConfirm(true);
                      }}
                      disabled={isBulkMakingPublic}
                      className="px-3 py-1.5 rounded-lg font-medium text-sm transition-colors flex items-center gap-1.5 bg-green-100 text-green-700 hover:bg-green-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isBulkMakingPublic ? (
                        <SpinnerIcon className="w-4 h-4" />
                      ) : (
                        <GlobeIcon className="w-4 h-4" />
                      )}
                      Make All Public
                    </button>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-3">
                  <button
                    onClick={toggleSelectionMode}
                    className="text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors"
                  >
                    Cancel
                  </button>
                  <div className="h-5 w-px bg-gray-300" />
                  <span className="text-sm text-gray-600 font-medium">
                    {selectedIds.length} selected
                  </span>
                  {selectedIds.length > 0 && (
                    <button
                      onClick={deselectAll}
                      className="text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors"
                    >
                      Deselect All
                    </button>
                  )}
                </div>
              )}
            </div>

            <div className="flex items-center gap-2">
              {isSelectionMode && selectedIds.length > 0 && (
                <>
                  {/* Add to Collection */}
                  <button
                    onClick={() => setShowAddToCollectionModal(true)}
                    className="bg-cyan-600 hover:bg-cyan-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M7 3a1 1 0 000 2h6a1 1 0 100-2H7zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM2 11a2 2 0 012-2h12a2 2 0 012 2v4a2 2 0 01-2 2H4a2 2 0 01-2-2v-4z" />
                    </svg>
                    Collection ({selectedIds.length})
                  </button>

                  {/* Make Public */}
                  <button
                    onClick={() => {
                      setBulkPublicTarget("selection");
                      setShowBulkPublicConfirm(true);
                    }}
                    disabled={isBulkMakingPublic}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isBulkMakingPublic ? (
                      <SpinnerIcon className="w-4 h-4" />
                    ) : (
                      <GlobeIcon className="w-4 h-4" />
                    )}
                    Make Public ({selectedIds.length})
                  </button>

                  {/* Share button - available for all content types */}
                  <button
                    onClick={() => setShowShareModal(true)}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-2"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    Share ({selectedIds.length})
                  </button>

                  {/* Create Post - only for images */}
                  {activeContentType === "images" && (
                    <button
                      onClick={handleCreatePost}
                      className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-2"
                    >
                      <PlusIcon className="w-4 h-4" />
                      {ENTITY_CONFIG.post.createLabel} ({selectedIds.length})
                    </button>
                  )}

                  {/* Process with AI - only for images */}
                  {activeContentType === "images" && (
                    <button
                      onClick={handleBatchProcess}
                      disabled={isProcessing}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      {isProcessing ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          Process with AI
                        </>
                      )}
                    </button>
                  )}

                  {/* Bulk rotate - only for videos */}
                  {activeContentType === "videos" && (
                    <BulkRotateDropdown
                      selectedIds={selectedIds}
                      onComplete={() => {
                        setSelectedIds([]);
                        setIsSelectionMode(false);
                      }}
                    />
                  )}

                  {/* Bulk stage change - available for all content types */}
                  <BulkStageChangeDropdown
                    selectedIds={selectedIds}
                    onComplete={() => {
                      setSelectedIds([]);
                      setIsSelectionMode(false);
                    }}
                  />
                </>
              )}

              {!isSelectionMode && (
                <button
                  onClick={selectAll}
                  className="text-blue-600 hover:text-blue-700 font-medium text-sm transition-colors"
                >
                  Select All
                </button>
              )}
            </div>
          </div>
        </div>

      {/* Grid - fills remaining space */}
      <div className="min-h-0 overflow-hidden">
        {dataSource.isLoading ? (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 animate-spin text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </div>
              <Text variant="h3" className="mb-2">
                Loading media...
              </Text>
              <Text variant="body" color="tertiary">
                Please wait while we fetch your media
              </Text>
            </div>
          </div>
        ) : currentItems.length > 0 ? (
          <ScrollableContainer height="100%" padding="none" scrollRef={scrollRootRef}>
            {/* Desktop/Tablet: Multi-column grid with gaps */}
            <div className="hidden sm:block">
              <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(clamp(200px, calc((100% - 2rem) / 3), 320px), 1fr))' }}>
                {renderGrid(currentItems, handleStageChange, undefined, {
                  isActive: isSelectionMode,
                  selectedIds,
                  onToggle: toggleSelection,
                }, handleAddToCollection)}
              </div>
            </div>

            {/* Mobile: 3-column Instagram-style grid (edge-to-edge, no gaps) */}
            <div className="block sm:hidden">
              <MediaGridMobile>
                {renderGrid(currentItems, handleStageChange, undefined, {
                  isActive: isSelectionMode,
                  selectedIds,
                  onToggle: toggleSelection,
                }, handleAddToCollection)}
              </MediaGridMobile>
            </div>

            {/* Infinite-scroll sentinel: triggers the next page before the
                user reaches the bottom. The button below stays as a fallback. */}
            <div ref={infiniteScrollSentinelRef} aria-hidden="true" />

            {/* Load More (fallback if the observer can't run) */}
            {dataSource.hasMore && (
              <div className="flex justify-center py-3">
                <button
                  onClick={dataSource.loadMore}
                  disabled={dataSource.isLoadingMore}
                  className="text-sm text-gray-500 hover:text-gray-700 transition-colors duration-200 disabled:text-gray-400"
                >
                  {dataSource.isLoadingMore ? (
                    <span className="flex items-center">
                      <svg
                        className="w-4 h-4 mr-1.5 animate-spin"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      Loading more...
                    </span>
                  ) : (
                    <span className="flex items-center">
                      Load more
                      <ChevronDownIcon className="w-4 h-4 ml-1" />
                    </span>
                  )}
                </button>
              </div>
            )}
          </ScrollableContainer>
        ) : (
          <div className="flex items-center justify-center py-16">
            <div className="text-center">
              <svg
                className="w-16 h-16 mx-auto mb-4 text-gray-300"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <Text variant="h3">No media found</Text>
            </div>
          </div>
        )}
      </div>
      </div>

      {/* Collections Sidebar - only in collection mode */}
      {isCollectionMode && selectedClient && (
        <CollectionsSidebar
          clientId={selectedClient.id}
          onClose={exitCollectionMode}
        />
      )}
      </div>

      {/* Media Share Modal */}
      {selectedClient && (
        <MediaShareModal
          isOpen={showShareModal}
          onClose={() => {
            setShowShareModal(false);
            setIsSelectionMode(false);
            setSelectedIds([]);
          }}
          selectedIds={selectedIds}
          clientId={selectedClient.id}
        />
      )}

      {/* Upload Media Modal */}
      {selectedClient && (
        <UploadMediaModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          clientId={selectedClient.id}
          contentType={
            isCollectionMode && activeCollectionId
              ? "auto"
              : activeContentType === "images"
                ? "photos"
                : activeContentType === "post-videos"
                  ? "post_video"
                  : activeContentType === "videos"
                    ? "videos"
                    : activeContentType === "audio"
                      ? "audio"
                      : activeContentType === "stories"
                        ? "stories"
                        : activeContentType === "reels"
                          ? "reels"
                          : "auto"
          }
          collectionId={isCollectionMode && activeCollectionId ? activeCollectionId : undefined}
          onUploadComplete={() => {
            setShowUploadModal(false);
            dataSource.refresh();
          }}
        />
      )}

      {/* Add to Collection Modal */}
      {selectedClient && (
        <AddToCollectionModal
          isOpen={showAddToCollectionModal}
          onClose={() => {
            setShowAddToCollectionModal(false);
            setAddToCollectionItemId(null);
          }}
          clientId={selectedClient.id}
          selectedIds={addToCollectionItemId ? [addToCollectionItemId] : selectedIds}
          onComplete={() => {
            setShowAddToCollectionModal(false);
            if (addToCollectionItemId) {
              setAddToCollectionItemId(null);
            } else {
              setIsSelectionMode(false);
              setSelectedIds([]);
            }
          }}
        />
      )}

      {/* Bulk Make Public Confirmation */}
      <ConfirmDialog
        isOpen={showBulkPublicConfirm}
        onClose={() => setShowBulkPublicConfirm(false)}
        onConfirm={handleBulkPublicConfirm}
        title="Make Items Public"
        message={
          bulkPublicTarget === "selection"
            ? `Make ${selectedIds.length} selected item(s) public? Images will be processed in the background.`
            : `Make every non-public item matching the current filter public? This covers all matches, not just the ones loaded on screen. They'll be processed in the background.`
        }
        confirmText="Make Public"
        variant="info"
        isLoading={isBulkMakingPublic}
      />
      </PageSection.Canvas>

      {/* Extra content (modals, etc.) */}
      {extraContent}
    </PageLayout>
  );
};
